import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Calendar, MapPin, Users, Trophy, CheckCircle2, X, Navigation } from "lucide-react";
import { toast, Toaster } from "sonner";
import PhoneInput from "../components/PhoneInput";
import { checkPhone, fullPhone } from "../lib/validation";
import MapEmbed, { extractEmbedSrc, searchDirectionsHref } from "../components/MapEmbed";
import ResponsiveImage from "../components/ResponsiveImage";
import { ddmmyyyy, ddmmyyyyRange } from "../lib/dates";
import { openRazorpayCheckout } from "../lib/razorpay";
import { useAuth } from "../context/AuthContext";

export default function EventDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [event, setEvent] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [confirmed, setConfirmed] = useState(null);
    const [form, setForm] = useState({ team_name: "", captain_name: "", captain_phone: "", members: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get(`/events/${id}`).then(({ data }) => setEvent(data));
    }, [id]);

    const submit = async (e) => {
        e.preventDefault();
        if (!checkPhone(form.captain_phone).ok) { toast.error("Captain phone must be 10 digits starting with 6/7/8/9"); return; }
        setSubmitting(true);
        try {
            const members = form.members.split(",").map(s => s.trim()).filter(Boolean);

            // 1) Ask backend to create a Razorpay order for the 50% advance fee.
            //    If Razorpay isn't configured server-side we fall back to direct register (dev/mock).
            let rzpFields = {};
            try {
                const { data: order } = await api.post(`/events/${id}/create-order`);
                // 2) Open Razorpay checkout.
                const res = await openRazorpayCheckout({
                    keyId: order.key_id,
                    orderId: order.order_id,
                    amount: order.amount,
                    currency: order.currency,
                    user: { name: form.captain_name, email: user?.email || "", phone: fullPhone(form.captain_phone) },
                    name: "Drive or Kick — Event",
                    description: `Advance for ${event.title}`,
                });
                rzpFields = {
                    razorpay_order_id: res.orderId,
                    razorpay_payment_id: res.paymentId,
                    razorpay_signature: res.signature,
                };
            } catch (orderErr) {
                // 503 = Razorpay not configured on server → continue to mock register
                if (orderErr.response?.status !== 503) {
                    throw orderErr;
                }
            }

            // 3) Finalise registration with the verified payment payload.
            const { data } = await api.post(`/events/${id}/register`, {
                team_name: form.team_name,
                captain_name: form.captain_name,
                captain_phone: fullPhone(form.captain_phone),
                members,
                ...rzpFields,
            });
            setConfirmed(data);
            setShowForm(false);
            toast.success("Team registered & paid!");
        } catch (err) {
            const detail = err.response?.data?.detail || err.message;
            toast.error(typeof detail === "string" ? detail : "Registration failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (!event) return <div className="p-8 text-center text-slate-400">Loading...</div>;
    const spotsLeft = event.max_teams - (event.registrations_count || 0);

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
            <Toaster theme="dark" richColors position="top-center" />
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4 text-sm">
                <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="rounded-lg overflow-hidden border border-slate-800 mb-6">
                <div className="relative h-56 sm:h-72 lg:h-[360px]">
                    <ResponsiveImage
                        src={event.photo_url}
                        alt={event.title}
                        eager
                        testid="event-cover-image"
                        className="absolute inset-0 w-full h-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-transparent" />
                    <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                        {event.is_completed && (
                            <span data-testid="event-detail-completed-badge"
                                className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-slate-700/95 text-slate-100 border border-slate-500 text-[10px] font-bold uppercase tracking-widest">
                                Completed
                            </span>
                        )}
                        {!event.is_completed && event.few_spots_left && (
                            <span data-testid="event-detail-fewspots-badge"
                                className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-amber-500/95 text-slate-950 border border-amber-300 text-[10px] font-bold uppercase tracking-widest animate-pulse">
                                Few spots left
                            </span>
                        )}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="text-[10px] font-bold tracking-[0.4em] uppercase text-[#00FF66]">{event.sport} · Tournament</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mt-1">{event.title}</h1>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                <Stat icon={Calendar} label="Dates" value={ddmmyyyyRange(event.start_date, event.end_date)} />
                <Stat icon={Users} label="Teams" value={`${event.registrations_count}/${event.max_teams}`} highlight={spotsLeft > 0} />
                <Stat icon={Trophy} label="Prize" value={event.prize || "TBA"} />
            </div>

            <Section title="About">
                <p className="text-slate-300 text-sm leading-relaxed">{event.description}</p>
            </Section>

            {event.rules && (
                <Section title="Rules">
                    <p className="text-slate-300 text-sm leading-relaxed">{event.rules}</p>
                </Section>
            )}

            {/* Mod 4 — Event venue with map embed iframe + Get Directions */}
            {(event.venue_name || event.venue_address || event.map_embed_url || event.centre) && (() => {
                const embedRaw = event.map_embed_url || event.centre?.map_embed_url || "";
                const embedSrc = extractEmbedSrc(embedRaw);
                const directionsAddress = event.venue_address || (event.centre ? `${event.centre.name} ${event.centre.address || ""} ${event.centre.city || ""}`.trim() : event.title);
                return (
                    <Section title="Venue">
                        <div className="space-y-3" data-testid="event-venue-block">
                            <div className="flex items-start gap-2 text-slate-300 text-sm">
                                <MapPin className="w-4 h-4 text-[#00FF66] mt-0.5 flex-shrink-0" />
                                <div>
                                    {event.venue_name && (
                                        <div className="text-white font-semibold">{event.venue_name}</div>
                                    )}
                                    <div>
                                        {event.venue_address || (event.centre && `${event.centre.name} · ${event.centre.address}, ${event.centre.city}`)}
                                    </div>
                                </div>
                            </div>
                            {embedSrc && (
                                <MapEmbed
                                    url={embedSrc}
                                    height={240}
                                    label={event.venue_name || event.title}
                                    showDirections={false}
                                    testid="event-map-view"
                                />
                            )}
                            <a
                                href={searchDirectionsHref(directionsAddress)}
                                target="_blank" rel="noopener noreferrer"
                                data-testid="get-directions-btn"
                                className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66]/10 border border-[#00FF66]/40 hover:bg-[#00FF66]/20 rounded text-xs font-bold uppercase tracking-widest text-[#00FF66]">
                                <Navigation className="w-3.5 h-3.5" /> Get Directions
                            </a>
                        </div>
                    </Section>
                );
            })()}

            <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-slate-500">Registration Fee</div>
                    <div className="font-display text-3xl font-bold text-[#00FF66]">₹{event.registration_fee}</div>
                    <div className="text-xs text-slate-400 mt-1">50% advance · Balance at venue</div>
                </div>
                <button
                    onClick={() => setShowForm(true)}
                    disabled={spotsLeft <= 0}
                    data-testid="register-team-btn"
                    className="px-6 h-12 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52] active:scale-95 transition disabled:opacity-40 glow-green"
                >
                    {spotsLeft > 0 ? `Register Team · Pay ₹${Math.floor(event.registration_fee / 2)}` : "Event Full"}
                </button>
            </div>

            {showForm && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <form onSubmit={submit} className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3 relative" data-testid="event-register-form">
                        <button type="button" onClick={() => setShowForm(false)} className="absolute top-3 right-3 text-slate-500 hover:text-white">
                            <X className="w-5 h-5" />
                        </button>
                        <h3 className="font-display text-2xl font-bold uppercase text-white">Register your Team</h3>
                        <Input label="Team Name" value={form.team_name} onChange={v => setForm({ ...form, team_name: v })} testid="team-name" />
                        <Input label="Captain Name" value={form.captain_name} onChange={v => setForm({ ...form, captain_name: v })} testid="captain-name" />
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Captain Phone</label>
                            <PhoneInput value={form.captain_phone} onChange={v => setForm({ ...form, captain_phone: v })} testid="captain-phone" />
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Members (comma-separated names)</label>
                            <textarea value={form.members} onChange={(e) => setForm({ ...form, members: e.target.value })}
                                rows={2} placeholder="Rohit, Priya, Karan..." data-testid="team-members"
                                className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                        </div>
                        <div className="bg-slate-950 border border-slate-800 rounded p-3 text-sm">
                            <div className="flex justify-between text-slate-300"><span>Total fee</span><span>₹{event.registration_fee}</span></div>
                            <div className="flex justify-between text-[#00FF66] font-bold mt-1"><span>Pay now (50%)</span><span>₹{Math.floor(event.registration_fee / 2)}</span></div>
                            <div className="flex justify-between text-amber-400 text-xs mt-1"><span>Balance at venue</span><span>₹{event.registration_fee - Math.floor(event.registration_fee / 2)}</span></div>
                        </div>
                        <button type="submit" disabled={submitting} data-testid="submit-event-register"
                            className="w-full h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] disabled:opacity-50 glow-green">
                            {submitting ? "Opening payment..." : `Pay ₹${Math.floor(event.registration_fee / 2)} & Register`}
                        </button>
                    </form>
                </div>
            )}

            {confirmed && (
                <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-slate-800 rounded-lg p-8 max-w-md text-center" data-testid="event-register-confirmed">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#00FF66]/15 border-2 border-[#00FF66] flex items-center justify-center glow-green mb-4">
                            <CheckCircle2 className="w-8 h-8 text-[#00FF66]" />
                        </div>
                        <h3 className="font-display text-2xl font-bold uppercase text-white">You're In!</h3>
                        <p className="text-slate-400 text-sm mt-2">Team <b className="text-white">{confirmed.team_name}</b> registered for {event.title}.</p>
                        <div className="mt-4 bg-slate-950 border border-slate-800 rounded p-3 text-sm text-left">
                            <Row label="Paid" value={`₹${confirmed.fee_paid}`} valueClass="text-[#00FF66] font-bold" />
                            <Row label="Balance at venue" value={`₹${confirmed.balance_due}`} valueClass="text-amber-400" />
                        </div>
                        <button onClick={() => { setConfirmed(null); navigate("/events"); }}
                            className="mt-5 w-full h-10 bg-[#00FF66] text-black font-bold uppercase text-xs tracking-widest rounded">
                            Back to Events
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ icon: Icon, label, value, highlight }) {
    return (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">
                <Icon className="w-4 h-4 text-[#00FF66]" /> {label}
            </div>
            <div className={`mt-2 text-sm font-bold ${highlight ? "text-[#00FF66]" : "text-white"}`}>{value}</div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 mb-4">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-3">{title}</div>
            {children}
        </div>
    );
}

function Input({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">{label}</label>
            <input value={value} onChange={(e) => onChange(e.target.value)} required data-testid={testid}
                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
        </div>
    );
}

function Row({ label, value, valueClass = "" }) {
    return (
        <div className="flex justify-between mb-1">
            <span className="text-slate-400">{label}</span>
            <span className={valueClass || "text-white"}>{value}</span>
        </div>
    );
}
